import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminTasks.css';
import { StateContext } from '../../../../../StateProvider';

// Reuse existing icons from your project
import appointmentIcon from '../../../../../../assets/Console/Landing/appointmentIcon.png';
import guideIcon from '../../../../../../assets/Console/Landing/guideIcon.png';
import petIcon from '../../../../../../assets/Console/Landing/petIcon.png';
import eventIcon from '../../../../../../assets/Console/Landing/eventIcon.png';
import incidentIcon from '../../../../../../assets/Console/Landing/incidentIcon.png';
import supportIcon from '../../../../../../assets/Console/Landing/supportIcon.png';

import arrowIcon from '../../../../../../assets/Landing/arrowIcon.png';
import impactIcon from '../../../../../../assets/Landing/impactIcon.png';

import Footer from '../../../../../Footer';
import AnnouncementsFeed from '../../../Utils/AnnouncementsFeed';

function AdminTasks() {
  var state = useContext(StateContext) || {};
  var loggedIn = state.loggedIn;
  var role = state.role;
  var financialSummary = state.financialSummary;
  var kpisFromState = state.kpis;
  var todayStatsFromState = state.todayStats;

  var navigate = useNavigate();

  // Route guard
  useEffect(function () {
    if (!loggedIn) {
      navigate('/login');
      return;
    }
    if (role !== 'admin') {
      navigate('/console');
    }
  }, [loggedIn, role, navigate]);

  // Impact math
  var _useState1 = useState(0), totalAnimalsHelped = _useState1[0], setTotalAnimalsHelped = _useState1[1];

  useEffect(function () {
    var totalRevenue = 0;
    if (financialSummary && typeof financialSummary.totalRevenue === 'number') {
      totalRevenue = financialSummary.totalRevenue;
    }
    var animals = calculateAnimalsHelped(totalRevenue, 20);
    setTotalAnimalsHelped(animals);
  }, [financialSummary]);

  function calculateAnimalsHelped(totalRevenue, supportPerAnimal) {
    if (typeof totalRevenue !== 'number') totalRevenue = 0;
    if (typeof supportPerAnimal !== 'number' || supportPerAnimal <= 0) supportPerAnimal = 20;
    var allocatedAmount = totalRevenue * 0.10;
    var result = Math.floor(allocatedAmount / supportPerAnimal);
    if (result < 0) result = 0;
    return result;
  }

  // KPIs with safe fallbacks
  var adminKPIs = useMemo(function () {
    var revToday = 0;
    var bookingsToday = 0;
    var occupancy = 0;
    var openIncidents = 0;
    var groomingCapacity = 0;
    var newLeads = 0;
    var tasksDueToday = 0;

    if (todayStatsFromState && typeof todayStatsFromState.revenueToday === 'number') {
      revToday = todayStatsFromState.revenueToday;
    }
    if (todayStatsFromState && typeof todayStatsFromState.bookingsToday === 'number') {
      bookingsToday = todayStatsFromState.bookingsToday;
    }
    if (kpisFromState && typeof kpisFromState.daycareOccupancyPct === 'number') {
      occupancy = kpisFromState.daycareOccupancyPct;
    }
    if (kpisFromState && typeof kpisFromState.openIncidents === 'number') {
      openIncidents = kpisFromState.openIncidents;
    }
    if (kpisFromState && typeof kpisFromState.groomingCapacityUtilPct === 'number') {
      groomingCapacity = kpisFromState.groomingCapacityUtilPct;
    }
    if (kpisFromState && typeof kpisFromState.newLeadsToday === 'number') {
      newLeads = kpisFromState.newLeadsToday;
    }
    if (kpisFromState && typeof kpisFromState.tasksDueToday === 'number') {
      tasksDueToday = kpisFromState.tasksDueToday;
    }

    return {
      revToday: revToday,
      bookingsToday: bookingsToday,
      occupancy: occupancy,
      openIncidents: openIncidents,
      groomingCapacity: groomingCapacity,
      newLeads: newLeads,
      tasksDueToday: tasksDueToday
    };
  }, [kpisFromState, todayStatsFromState]);

  function handleNav(to) {
    navigate(to);
    try { window.scrollTo(0, 0); } catch (e) {}
  }

  function handleImpactClicked(e) {
    if (!loggedIn) {
      if (e && e.preventDefault) e.preventDefault();
      navigate('/login');
    }
  }

  function formatMoney(n) {
    if (typeof n !== 'number') return '$0';
    try { return '$' + n.toLocaleString(); } catch (e) { return '$' + String(n); }
  }

  function roundPct(n) {
    if (typeof n !== 'number') return 0;
    return Math.round(n);
  }

  return (
    <>
      <div className="admin-dashboard-container">
        <h4 className="console-header">Admin Task Board</h4>
        <h4 className="console-message">Clarity for the team and comfort for the pets. Let’s run the day right.</h4>

        {/* Quick Actions for Admins */}
        <div className="container store-menu-container">
          <div className="row">
            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={appointmentIcon}
                  alt="Task Assignments"
                  onClick={function () { handleNav('/console/tasks/admin/task-assignments'); }}
                />
              </div>
              <p className="store-menu-button-title">Task Manager</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={petIcon}
                  alt="Staff and Scheduling"
                  onClick={function () { handleNav('/console/tasks/admin/staff-and-schedule'); }}
                />
              </div>
              <p className="store-menu-button-title">Staff & Schedule</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={guideIcon}
                  alt="Finance"
                  onClick={function () { handleNav('/console/finance'); }}
                />
              </div>
              <p className="store-menu-button-title">Finance</p>
            </div>
          </div>

          <div className="row">
            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={incidentIcon}
                  alt="Inventory"
                  onClick={function () { handleNav('/console/inventory'); }}
                />
              </div>
              <p className="store-menu-button-title">Inventory</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={eventIcon}
                  alt="Announcements"
                  onClick={function () { handleNav('/console/announcements'); }}
                />
              </div>
              <p className="store-menu-button-title">Announcements</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={supportIcon}
                  alt="Payments"
                  onClick={function () { handleNav('/console/payments'); }}
                />
              </div>
              <p className="store-menu-button-title">Payments</p>
            </div>
          </div>

          <div className="row">
            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={incidentIcon}
                  alt="Incident Log"
                  onClick={function () { handleNav('/console/tasks/admin/incident-log'); }}
                />
              </div>
              <p className="store-menu-button-title">Incident Log</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={eventIcon}
                  alt="Events"
                  onClick={function () { handleNav('/console/events'); }}
                />
              </div>
              <p className="store-menu-button-title">Events</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={supportIcon}
                  alt="Help and Support"
                  onClick={function () { handleNav('/console/support'); }}
                />
              </div>
              <p className="store-menu-button-title">Help & Support</p>
            </div>
          </div>

          <div className="row">
            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={appointmentIcon}
                  alt="Performance"
                  onClick={function () { handleNav('/console/performance'); }}
                />
              </div>
              <p className="store-menu-button-title">Performance</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={guideIcon}
                  alt="Store Settings"
                  onClick={function () { handleNav('/console/store-settings'); }}
                />
              </div>
              <p className="store-menu-button-title">Store Settings</p>
            </div>

            <div className="col store-menu-button-container">
              <div className="store-button-image-container">
                <img
                  className="store-menu-button-image"
                  src={petIcon}
                  alt="Clients"
                  onClick={function () { handleNav('/console/pets'); }}
                />
              </div>
              <p className="store-menu-button-title">Clients</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Daily Brief */}
      <div className="staff-section-wrapper">
        <div className="staff-section-title">
          <span>📊</span> Admin Daily Brief
        </div>
        <div className="staff-section-grid">
          <div className="brief-card">Revenue Today: <strong>{formatMoney(adminKPIs.revToday)}</strong></div>
          <div className="brief-card">Bookings Today: <strong>{adminKPIs.bookingsToday}</strong></div>
          <div className="brief-card">Daycare Occupancy: <strong>{roundPct(adminKPIs.occupancy)}%</strong></div>
          <div className="brief-card">Grooming Utilization: <strong>{roundPct(adminKPIs.groomingCapacity)}%</strong></div>
          <div className="brief-card">Open Incidents: <strong>{adminKPIs.openIncidents}</strong></div>
          <div className="brief-card">New Leads Today: <strong>{adminKPIs.newLeads}</strong></div>
          <div className="brief-card">Tasks Due Today: <strong>{adminKPIs.tasksDueToday}</strong></div>
        </div>
      </div>

      <AnnouncementsFeed />

      <hr className="deals-breakline" />

      <div className="container">
        <Link to="/store/deals/impact" className="impact-highlight" onClick={handleImpactClicked}>
          <div className="impact-highlight-card">
            <div className="impact-highlight-card-items">
              <h3 className="impact-highlight-number">{totalAnimalsHelped}</h3>
              <h3 className="impact-highlight-paragraph">
                Animals helped. Your support enables us to contribute to animal rescue and care.
              </h3>
              <div className="impact-highlight-action">
                <h3 className="impact-highlight-button-title">SEE OUR IMPACT</h3>
                <img src={arrowIcon} className="impact-highlight-button" alt="see impact" />
              </div>
            </div>
            <div className="impact-highlight-card-items">
              <img className="impact-highlight-icon" src={impactIcon} alt="impact icon" />
            </div>
          </div>
        </Link>
      </div>

      <Footer />
    </>
  );
}

export default AdminTasks;
